import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ShoppingCart, ArrowLeft, Plus, Minus, Phone, Gift, Play, X } from 'lucide-react';
import { productsAPI } from '../services/api';
import { useCart } from '../contexts/CartContext';
import UpsellCard from '../components/UpsellCard';
import ProductCarousel from '../components/ProductCarousel';
import { usePageView } from '../hooks/useAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extrair ID do vídeo do YouTube
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/shorts\/([^&\n?#]+)/
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }
  return null;
};

const ProductDetail = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  
  // Sistema de variações simplificado
  const [selectedStorage, setSelectedStorage] = useState(null);
  const [selectedColorId, setSelectedColorId] = useState(null);
  
  const [upsells, setUpsells] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const { addToCart, toggleUpsell, isUpsellSelected, selectedUpsells } = useCart();

  // Track page view with product ID
  usePageView('product', id);

  // ID do vídeo do YouTube (se existir)
  const videoId = useMemo(() => {
    return product?.video_url ? getYouTubeVideoId(product.video_url) : null;
  }, [product?.video_url]);

  // Parse storage_variants (novo sistema simplificado)
  const storageVariants = useMemo(() => {
    if (!product?.storage_variants) return [];
    try {
      const variants = typeof product.storage_variants === 'string' 
        ? JSON.parse(product.storage_variants) 
        : product.storage_variants;
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  }, [product?.storage_variants]);

  // Parse color_variants (com álbum de imagens)
  const colorVariants = useMemo(() => {
    if (!product?.color_variants) return [];
    try {
      const variants = typeof product.color_variants === 'string' 
        ? JSON.parse(product.color_variants) 
        : product.color_variants;
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  }, [product?.color_variants]);

  // Verifica se usa sistema de variações
  const hasStorageVariants = storageVariants.length > 0;
  const hasColorVariants = colorVariants.length > 0;

  // Variante de storage selecionada
  const selectedStorageVariant = useMemo(() => {
    if (!hasStorageVariants || !selectedStorage) return null;
    return storageVariants.find(sv => sv.label === selectedStorage);
  }, [hasStorageVariants, storageVariants, selectedStorage]);

  // Variante de cor selecionada
  const selectedColorVariant = useMemo(() => {
    if (!hasColorVariants || !selectedColorId) return null;
    return colorVariants.find(cv => cv.id === selectedColorId);
  }, [hasColorVariants, colorVariants, selectedColorId]);

  // Imagens atuais baseadas na cor selecionada
  const currentImages = useMemo(() => {
    // Se tem cor selecionada com álbum de imagens
    if (selectedColorVariant?.images?.length > 0) {
      return selectedColorVariant.images;
    }
    
    // Imagens gerais do produto
    if (product?.images && product.images.length > 0) {
      const images = typeof product.images === 'string' 
        ? JSON.parse(product.images) 
        : product.images;
      if (images.length > 0) return images;
    }
    
    // Fallback para imagem principal única
    if (product?.image_url) {
      return [product.image_url];
    }
    
    return [];
  }, [selectedColorVariant, product]);

  // Preço atual (baseado no storage selecionado + ajuste de cor)
  const currentPrice = useMemo(() => {
    let basePrice = product?.price_pix || product?.price || 0;
    
    // Se tiver storage selecionado, usa o preço dele
    if (selectedStorageVariant?.price) {
      basePrice = selectedStorageVariant.price;
    }
    
    // Se ajuste de preço por cor estiver habilitado e houver cor selecionada com ajuste
    if (product?.enable_color_price_adjust && selectedColorVariant?.price_adjust) {
      basePrice += selectedColorVariant.price_adjust;
    }
    
    return basePrice;
  }, [selectedStorageVariant, selectedColorVariant, product]);

  // Preço original (riscado) - também considera ajuste de cor
  const originalPrice = useMemo(() => {
    let baseOriginal = null;
    
    if (selectedStorageVariant?.original_price) {
      baseOriginal = selectedStorageVariant.original_price;
    } else {
      baseOriginal = product?.price_card || product?.market_price || null;
    }
    
    // Se ajuste de preço por cor estiver habilitado e houver cor selecionada com ajuste
    if (baseOriginal && product?.enable_color_price_adjust && selectedColorVariant?.price_adjust) {
      baseOriginal += selectedColorVariant.price_adjust;
    }
    
    return baseOriginal;
  }, [selectedStorageVariant, selectedColorVariant, product]);

  // Estoque atual (baseado no storage selecionado)
  const currentStock = useMemo(() => {
    if (selectedStorageVariant?.stock !== undefined) {
      return selectedStorageVariant.stock;
    }
    return product?.stock || 1;
  }, [selectedStorageVariant, product]);

  // Carregar produto
  useEffect(() => {
    fetchProduct();
    fetchUpsells();
    fetchSettings();
  }, [id]);

  // Sincronizar URL com variantes de storage e cor (NOVO SISTEMA SIMPLIFICADO)
  useEffect(() => {
    if (product) {
      const urlStorage = searchParams.get('storage');
      const urlColor = searchParams.get('color');
      
      // Configura storage inicial
      if (hasStorageVariants) {
        let storage = urlStorage;
        if (!storage || !storageVariants.some(sv => sv.label === storage)) {
          // Seleciona primeiro storage com estoque
          const availableStorage = storageVariants.find(sv => sv.stock === undefined || sv.stock > 0);
          storage = availableStorage?.label || storageVariants[0]?.label || null;
        }
        setSelectedStorage(storage);
      }
      
      // Configura cor inicial
      if (hasColorVariants) {
        let colorId = urlColor;
        if (!colorId || !colorVariants.some(cv => cv.id === colorId)) {
          // Seleciona primeira cor disponível
          const availableColor = colorVariants.find(cv => cv.available !== false);
          colorId = availableColor?.id || colorVariants[0]?.id || null;
        }
        setSelectedColorId(colorId);
      }
    }
  }, [product, hasStorageVariants, hasColorVariants, storageVariants, colorVariants]);

  const fetchProduct = async () => {
    try {
      const response = await productsAPI.getById(id);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpsells = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/upsells`);
      if (response.ok) {
        const data = await response.json();
        setUpsells(data.filter(u => u.is_active));
      }
    } catch (error) {
      console.error('Error fetching upsells:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const getWhatsAppNumber = () => {
    return settings?.whatsapp_number || settings?.footer?.whatsapp || '5543996963570';
  };

  // Handler para selecionar storage
  const handleStorageSelect = useCallback((storageLabel) => {
    setSelectedStorage(storageLabel);
    setShowVideo(false);
  }, []);

  // Handler para selecionar cor
  const handleColorSelect = useCallback((colorId) => {
    setSelectedColorId(colorId);
    setShowVideo(false);
  }, []);

  const handleAddToCart = () => {
    // Prepara item para o carrinho
    const cartItem = {
      ...product,
      price: currentPrice,
      selectedVariant: selectedColorVariant ? {
        id: selectedColorVariant.id,
        name: selectedColorVariant.name,
        hex: selectedColorVariant.hex
      } : null,
      storage: selectedStorage || product?.storage || null
    };
    
    addToCart(cartItem, quantity);
  };

  const handleWhatsAppClick = () => {
    const phoneNumber = getWhatsAppNumber();
    const productTitle = product.title || `${product.brand} ${product.model}`;
    
    let message = `Olá! Tenho interesse no:\n\n`;
    message += `*${productTitle}*\n`;
    
    if (selectedStorage) {
      message += `Armazenamento: ${selectedStorage}\n`;
    }
    if (selectedColorVariant) {
      message += `Cor: ${selectedColorVariant.name}\n`;
    }
    
    message += `Valor: ${formatPrice(currentPrice)} no PIX\n`;
    
    if (quantity > 1) {
      message += `Quantidade: ${quantity}\n`;
    }
    
    if (selectedUpsells.length > 0) {
      message += `\n*Com adicionais:*\n`;
      selectedUpsells.forEach(u => {
        message += `+ ${u.title} — ${formatPrice(u.price)}\n`;
      });
    }
    
    message += `\nPode me passar mais informações?`;
    
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F10] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#C9A24D] mx-auto"></div>
          <p className="mt-4 text-[#B5B5B5]">Carregando produto...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#0F0F10] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#EAEAEA] mb-4">Produto não encontrado</h2>
          <Link to="/catalogo" className="text-[#C9A24D] hover:text-[#E0B85C] hover:underline">
            Voltar ao catálogo
          </Link>
        </div>
      </div>
    );
  }

  // Overlay do vídeo para o carrossel
  const videoOverlay = videoId && !showVideo ? (
    <button
      onClick={() => setShowVideo(true)}
      className="absolute bottom-4 right-4 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full flex items-center gap-2 shadow-lg transition-all hover:scale-105 z-10"
      data-testid="play-video-btn"
    >
      <Play className="h-5 w-5 fill-current" />
      <span className="font-medium text-sm">Ver Vídeo</span>
    </button>
  ) : null;

  return (
    <div className="min-h-screen bg-[#0F0F10]">
      {/* Breadcrumb / Voltar */}
      <div className="bg-[#1C1C1F] border-b border-[#2A2A2D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link 
            to="/catalogo" 
            className="inline-flex items-center space-x-2 text-[#C9A24D] hover:text-[#E0B85C] transition-colors"
            data-testid="back-to-catalog-btn"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Voltar ao Catálogo</span>
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          {/* Image Gallery / Carousel */}
          <div className="space-y-4">
            {showVideo && videoId ? (
              // Player do YouTube em fullscreen na área do carrossel
              <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl overflow-hidden aspect-square relative">
                <iframe
                  src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                  title="Vídeo do produto"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
                <button
                  onClick={() => setShowVideo(false)}
                  className="absolute top-3 right-3 bg-black/70 hover:bg-black text-white p-2 rounded-full transition-colors z-10"
                  data-testid="close-video-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            ) : (
              // Carrossel de imagens
              <ProductCarousel
                images={currentImages}
                productTitle={product.title || `${product.brand} ${product.model}`}
                initialSlide={0}
                overlayContent={videoOverlay}
              />
            )}

            {/* Video Thumbnail (se tiver vídeo) */}
            {videoId && !showVideo && (
              <button
                onClick={() => setShowVideo(true)}
                className="flex items-center gap-3 w-full p-3 bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl hover:border-red-500/50 transition-colors group"
                data-testid="video-thumbnail-btn"
              >
                <div className="relative w-20 h-14 rounded-lg overflow-hidden flex-shrink-0">
                  <img
                    src={`https://img.youtube.com/vi/${videoId}/mqdefault.jpg`}
                    alt="Vídeo"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors">
                    <div className="bg-red-600 rounded-full p-1.5">
                      <Play className="h-3 w-3 text-white fill-current" />
                    </div>
                  </div>
                </div>
                <div className="text-left">
                  <p className="text-[#EAEAEA] font-medium text-sm">Ver vídeo do produto</p>
                  <p className="text-[#666] text-xs">Clique para assistir</p>
                </div>
              </button>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Badges */}
            <div className="flex items-center space-x-3">
              <span className={`px-4 py-1.5 rounded-full text-sm font-semibold ${
                product.condition === 'new' || product.condition === 'novo'
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
              }`}>
                {product.condition === 'new' || product.condition === 'novo' ? 'Novo' : 'Seminovo'}
              </span>
              {(product.featured || product.is_featured) && (
                <span className="bg-[#C9A24D]/20 text-[#C9A24D] border border-[#C9A24D]/30 px-4 py-1.5 rounded-full text-sm font-bold">
                  Destaque
                </span>
              )}
            </div>

            {/* Title */}
            <div>
              <h1 className="text-cinzel text-3xl md:text-4xl font-bold text-[#EAEAEA] mb-2" data-testid="product-title">
                {product.title || product.brand}
              </h1>
              {product.model && (
                <p className="text-xl md:text-2xl text-[#B5B5B5]">{product.model}</p>
              )}
              {/* Mostra storage legado se não tem variações */}
              {!hasStorageVariants && product.storage && (
                <p className="text-lg text-[#C9A24D] mt-1">{product.storage}</p>
              )}
            </div>

            {/* Seletor de Armazenamento (novo sistema) */}
            {hasStorageVariants && (
              <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4 md:p-5">
                <h4 className="text-sm font-semibold text-[#B5B5B5] mb-3 uppercase tracking-wider">
                  Armazenamento
                </h4>
                <div className="flex flex-wrap gap-2">
                  {storageVariants.map((sv) => {
                    const isSelected = sv.label === selectedStorage;
                    const hasStockForStorage = sv.stock === undefined || sv.stock > 0;
                    return (
                      <button
                        key={sv.label}
                        onClick={() => handleStorageSelect(sv.label)}
                        disabled={!hasStockForStorage}
                        className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                          isSelected
                            ? 'bg-[#C9A24D] text-[#0F0F10] ring-2 ring-[#C9A24D] ring-offset-2 ring-offset-[#1C1C1F]'
                            : hasStockForStorage
                              ? 'bg-[#2A2A2D] text-[#EAEAEA] hover:bg-[#3A3A3D]'
                              : 'bg-[#1C1C1F] text-[#666] cursor-not-allowed line-through'
                        }`}
                        data-testid={`storage-option-${sv.label}`}
                      >
                        {sv.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Seletor de Cor (novo sistema) */}
            {hasColorVariants && (
              <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                <h4 className="text-sm font-semibold text-[#B5B5B5] mb-3 uppercase tracking-wider">
                  Cor
                </h4>
                <div className="flex flex-wrap gap-3">
                  {colorVariants.map((cv) => {
                    const isSelected = cv.id === selectedColorId;
                    const isAvailable = cv.available !== false;
                    return (
                      <button
                        key={cv.id}
                        onClick={() => handleColorSelect(cv.id)}
                        disabled={!isAvailable}
                        className={`group relative ${!isAvailable ? 'cursor-not-allowed opacity-50' : ''}`}
                        title={cv.name}
                        data-testid={`color-option-${cv.id}`}
                      >
                        <div
                          className={`w-10 h-10 rounded-full border-2 transition-all ${
                            isSelected
                              ? 'border-[#C9A24D] ring-2 ring-[#C9A24D] ring-offset-2 ring-offset-[#1C1C1F]'
                              : 'border-[#3A3A3D] hover:border-[#5A5A5D]'
                          }`}
                          style={{ backgroundColor: cv.hex || '#333' }}
                        />
                        {!isAvailable && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-full h-0.5 bg-red-500 rotate-45" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                {selectedColorVariant && (
                  <p className="text-[#EAEAEA] text-sm mt-3">
                    {selectedColorVariant.name}
                    {selectedColorVariant.images?.length > 1 && (
                      <span className="text-[#666] ml-2">
                        ({selectedColorVariant.images.length} fotos)
                      </span>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Price Section - Otimizado para conversão */}
            <div className="space-y-3">
              {/* Preço PIX em destaque */}
              <div className="bg-gradient-to-r from-[#1C1C1F] to-[#252528] border-2 border-[#C9A24D] rounded-xl p-5 md:p-6 relative overflow-hidden">
                {/* Badge de destaque */}
                <div className="absolute top-0 right-0">
                  <div className="bg-[#C9A24D] text-[#0F0F10] text-xs font-bold px-3 py-1 rounded-bl-lg">
                    MELHOR PREÇO
                  </div>
                </div>
                
                {/* Indicador "A partir de" se tiver variações e nenhuma selecionada */}
                {hasStorageVariants && !selectedStorage && (
                  <p className="text-[#B5B5B5] text-sm mb-1">A partir de</p>
                )}
                
                {/* Preço PIX principal */}
                <p className="text-[#C9A24D] font-bold text-4xl md:text-5xl" data-testid="product-price">
                  {formatPrice(currentPrice)}
                </p>
                <p className="text-[#EAEAEA] font-semibold text-lg mt-1">
                  no PIX
                </p>
                
                {/* Parcelamento baseado no preço original */}
                {originalPrice && originalPrice > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#3A3A3D]">
                    <p className="text-[#B5B5B5] text-base">
                      ou <span className="text-[#EAEAEA] font-semibold">10x sem juros</span> de{' '}
                      <span className="text-[#EAEAEA] font-bold text-lg">
                        {formatPrice(originalPrice / 10)}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Selos de benefícios */}
              <div className="flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 bg-[#C9A24D]/10 border border-[#C9A24D]/30 text-[#C9A24D] px-3 py-1.5 rounded-full text-xs font-semibold">
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  10x sem juros
                </span>
                {(product.stock > 0 || product.stock_status === 'pronta entrega') && (
                  <span className="inline-flex items-center gap-1.5 bg-green-500/10 border border-green-500/30 text-green-400 px-3 py-1.5 rounded-full text-xs font-semibold">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    Pronta entrega
                  </span>
                )}
                {originalPrice && originalPrice > currentPrice && (
                  <span className="inline-flex items-center gap-1.5 bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-1.5 rounded-full text-xs font-semibold">
                    -{Math.round((1 - currentPrice / originalPrice) * 100)}% OFF
                  </span>
                )}
              </div>
            </div>

            {/* Stock - Apenas para status especiais */}
            {product.stock_status === 'sob encomenda' && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 font-semibold">Sob encomenda - consulte prazo</p>
              </div>
            )}
            {product.stock <= 0 && product.stock_status === 'esgotado' && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-red-400 font-semibold">Produto fora de estoque</p>
              </div>
            )}

            {/* Quantity Selector */}
            {(product.stock > 0 || product.stock_status !== 'esgotado') && (
              <div>
                <label className="block text-sm font-semibold text-[#EAEAEA] mb-2 uppercase tracking-wider">
                  Quantidade
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="bg-[#2A2A2D] hover:bg-[#3A3A3D] text-[#EAEAEA] p-3 rounded-lg transition-colors border border-[#3A3A3D]"
                    disabled={quantity <= 1}
                    data-testid="decrease-quantity-btn"
                  >
                    <Minus className="h-5 w-5" />
                  </button>
                  <span className="text-2xl font-bold text-[#EAEAEA] w-12 text-center" data-testid="quantity-display">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    className="bg-[#2A2A2D] hover:bg-[#3A3A3D] text-[#EAEAEA] p-3 rounded-lg transition-colors border border-[#3A3A3D]"
                    data-testid="increase-quantity-btn"
                  >
                    <Plus className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Upsells Section */}
            {upsells.length > 0 && (
              <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="h-5 w-5 text-[#C9A24D]" />
                  <h3 className="text-[#EAEAEA] font-semibold">Adicione ao seu pedido</h3>
                </div>
                <div className="space-y-2">
                  {upsells.slice(0, 3).map(upsell => (
                    <UpsellCard
                      key={upsell.id}
                      upsell={upsell}
                      isSelected={isUpsellSelected(upsell.id)}
                      onToggle={toggleUpsell}
                      compact={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleWhatsAppClick}
                className="w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-3 transition-all duration-200 bg-[#25D366] hover:bg-[#20BA5A] text-white shadow-lg hover:shadow-[0_8px_30px_rgba(37,211,102,0.3)]"
                data-testid="whatsapp-button"
              >
                <Phone className="h-6 w-6" />
                <span>Comprar pelo WhatsApp</span>
              </button>

              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0 && product.stock_status === 'esgotado'}
                className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center space-x-3 transition-all duration-200 ${
                  product.stock > 0 || product.stock_status !== 'esgotado'
                    ? 'bg-[#C9A24D] text-[#0F0F10] hover:bg-[#E0B85C] shadow-lg hover:shadow-[0_8px_30px_rgba(201,162,77,0.3)]'
                    : 'bg-[#2A2A2D] text-[#666] cursor-not-allowed border border-[#3A3A3D]'
                }`}
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="h-6 w-6" />
                <span>{product.stock > 0 || product.stock_status !== 'esgotado' ? 'Adicionar ao Carrinho' : 'Indisponível'}</span>
              </button>
            </div>

            {/* Description */}
            {product.description && (
              <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
                <h3 className="text-cinzel text-xl font-bold text-[#EAEAEA] mb-3 uppercase tracking-wider">Descrição</h3>
                <p className="text-[#B5B5B5] leading-relaxed">{product.description}</p>
              </div>
            )}

            {/* Specifications */}
            {product.specifications && (
              <div className="bg-[#1C1C1F] border border-[#2A2A2D] rounded-xl p-6">
                <h3 className="text-cinzel text-xl font-bold text-[#EAEAEA] mb-4 uppercase tracking-wider">Especificações</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {product.specifications.screen && (
                    <div className="border-b border-[#2A2A2D] pb-3">
                      <p className="text-sm text-[#666] uppercase tracking-wider">Tela</p>
                      <p className="font-semibold text-[#EAEAEA]">{product.specifications.screen}</p>
                    </div>
                  )}
                  {product.specifications.processor && (
                    <div className="border-b border-[#2A2A2D] pb-3">
                      <p className="text-sm text-[#666] uppercase tracking-wider">Processador</p>
                      <p className="font-semibold text-[#EAEAEA]">{product.specifications.processor}</p>
                    </div>
                  )}
                  {product.specifications.ram && (
                    <div className="border-b border-[#2A2A2D] pb-3">
                      <p className="text-sm text-[#666] uppercase tracking-wider">Memória RAM</p>
                      <p className="font-semibold text-[#EAEAEA]">{product.specifications.ram}</p>
                    </div>
                  )}
                  {product.specifications.storage && (
                    <div className="border-b border-[#2A2A2D] pb-3">
                      <p className="text-sm text-[#666] uppercase tracking-wider">Armazenamento</p>
                      <p className="font-semibold text-[#EAEAEA]">{product.specifications.storage}</p>
                    </div>
                  )}
                  {product.specifications.camera && (
                    <div className="border-b border-[#2A2A2D] pb-3">
                      <p className="text-sm text-[#666] uppercase tracking-wider">Câmera</p>
                      <p className="font-semibold text-[#EAEAEA]">{product.specifications.camera}</p>
                    </div>
                  )}
                  {product.specifications.battery && (
                    <div className="border-b border-[#2A2A2D] pb-3">
                      <p className="text-sm text-[#666] uppercase tracking-wider">Bateria</p>
                      <p className="font-semibold text-[#EAEAEA]">{product.specifications.battery}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Botão Flutuante WhatsApp - Mobile Only */}
      <div className="md:hidden">
        <a
          href={`https://wa.me/${getWhatsAppNumber()}?text=${encodeURIComponent(`Olá! Vim pelo site e tenho interesse no ${product.title || product.brand + ' ' + product.model}.`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-6 right-6 z-50 bg-[#25D366] text-white p-3 rounded-full shadow-2xl flex items-center justify-center"
          aria-label="WhatsApp"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className="h-9 w-9 fill-white">
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/>
          </svg>
        </a>
      </div>
    </div>
  );
};

export default ProductDetail;
